/**
 * Receipt preview component — the visual paper receipt mock.
 * Extracted from customer-receipt-form.tsx for file size reduction.
 */

import type { LocationById, TipsSettings } from "~/api-refs";

interface CustomerReceiptPreviewProps {
  showOrderName: boolean;
  showServerName: boolean;
  showSeatNumbers: boolean;
  showModifiersBreakdown: boolean;
  showLocationNameAndLogo: boolean;
  customText: string | null;
  indicateCustomerOrOrganizationCopy: boolean;
  location: LocationById;
  tipsSettings: Pick<TipsSettings, "tipMethod" | "suggestedAmounts">;
}

export function CustomerReceiptPreview({
  showOrderName,
  showServerName,
  showSeatNumbers,
  showModifiersBreakdown,
  showLocationNameAndLogo,
  customText,
  indicateCustomerOrOrganizationCopy,
  location,
  tipsSettings,
}: CustomerReceiptPreviewProps) {
  return (
    <div className="bg-background-subtle rounded-lg p-6">
      <div className="caption bg-background text-foreground shadow-ambient w-56 rounded-md px-3.5 py-7 font-mono">
        <div className="text-center">
          Order: B1
          <br />
          Dine In
          {showServerName && (
            <>
              <br />
              Server: John
            </>
          )}
          <br />
          05/01/2024 4:20 PM
          {showOrderName && (
            <>
              <br />
              Table 3
            </>
          )}
        </div>
        <hr className="bg-border my-3.5 h-px border-none" />
        <div className="whitespace-pre">{`${showSeatNumbers ? "Seat 1\n" : ""}2 Cheeseburger X $8.00    $16.00
${
  showModifiersBreakdown
    ? `1 Chicken Burger           $8.00
  2 Bacon X $0.50          $1.00
  1 Tomato                 $0.00`
    : "1 Chicken Burger           $9.00"
}
${
  showModifiersBreakdown
    ? `1 Houseburger             $14.00
  - Double Patty           $4.00
  - Ketchup
    1 Regular              $0.00
    1 Spicy                $0.50
  - Fries
    - Cajun Spice          $2.00`
    : "1 Houseburger             $20.50"
}${showSeatNumbers ? "\nSeat 2" : ""}
1 Caesar Salad             $6.00
${
  showModifiersBreakdown
    ? `1 Latte                    $3.00
  - Regular                $0.00
  1 Vanilla                $1.00`
    : "1 Latte                    $4.00"
}
Subtotal                  $55.50`}</div>
        <hr className="bg-border my-3.5 h-px border-none" />
        <div className="whitespace-pre">{`Discount: Reward 10%      -$5.55
Discount: Loyalty Club    -$5.00
Tuesdays $5
Tax: Prepared Food 10%     $5.15
Tax: Beverages 8%          $0.32`}</div>
        <hr className="bg-border my-3.5 h-px border-none" />
        <div className="mb-2 font-bold whitespace-pre">
          {"Bill Total:               $51.32"}
        </div>
        {tipsSettings.tipMethod === "in-pos" && (
          <>
            <div className="mb-2 whitespace-pre">
              {"Tip                       $10.00"}
            </div>
            <div className="mb-2 font-bold whitespace-pre">
              {"Grand Total:              $61.32"}
            </div>
          </>
        )}
        {tipsSettings.tipMethod === "on-receipt" && (
          <>
            <div className="text-right whitespace-pre">
              {"+ Tip: __________\n\n= Grand Total: __________\n\n\n"}
            </div>
            <hr className="bg-border mx-auto h-px w-4/5 border-none" />
            <div className="text-2xs mb-2 text-center">Customer signature</div>
            {tipsSettings.suggestedAmounts?.map((suggestion) => (
              <div
                className="text-center"
                key={`tip-${suggestion.type}-${suggestion.value.toString()}`}
              >
                Tip{" "}
                {suggestion.type === "percentage"
                  ? `${suggestion.value}%`
                  : `${suggestion.value}`}{" "}
                ={" "}
                {(suggestion.type === "percentage"
                  ? (suggestion.value / 100) * 51.32
                  : suggestion.value
                ).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
            ))}
          </>
        )}
        {(showLocationNameAndLogo ||
          !!customText ||
          indicateCustomerOrOrganizationCopy) && (
          <>
            <hr className="bg-border my-3.5 h-px border-none" />
            <div className="flex flex-col items-center justify-center gap-2">
              {indicateCustomerOrOrganizationCopy && (
                <p className="text-center">Customer Copy</p>
              )}
              {showLocationNameAndLogo && (
                <>
                  <p>{location.name.slice(0, 32)}</p>
                  <img
                    alt={`${location.name} logo`}
                    className="size-10 rounded-full saturate-0"
                    height={40}
                    src={
                      location.logoUrl ??
                      "/images/location-logo-placeholder.svg"
                    }
                    width={40}
                  />
                </>
              )}
              {customText && (
                <p className="max-w-[32ch] text-center break-all">
                  {customText}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
